import styled from "@emotion/styled";
import { useState } from "react";

import { trackEvent, TrackedEvent } from "@/domains/metrics";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonSize,
  MDSButtonVariant,
  MDSCard,
  MDSCardVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSIcon,
  MDSIconColor,
  MDSModalDialog,
  MDSSeparator,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { copyUrlToClipboard } from "@/actions/copyUrlToClipboard";
import type { RoutineRowViewModel } from "@/pages/agent/routines/agent-routines-page.utils";
import { formatAgentDate } from "@/pages/agent/utils/agent-date.utils";
import { formatStatusLabel, getAgentStatusIconKind } from "@/pages/agent/utils/agent-status.utils";
import {
  createRoutineShareLink,
  getActiveRoutineShareLinkForSourceRoutine,
  revokeRoutineShareLink,
  type RoutineShareLink,
} from "@/pages/routine-share/routine-share-api";
import { toastModule } from "@/modules/toast";
import { AssistantRoutineStatus } from "@/store/assistant/types";
import { DeleteAssistantRoutineOperation } from "@/store/sync/operations/assistant-routines/DeleteAssistantRoutineOperation";
import { UpdateAssistantRoutineOperation } from "@/store/sync/operations/assistant-routines/UpdateAssistantRoutineOperation";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";

export const AgentRoutineCard = ({
  routineRow,
  isRoutineSharingEnabled,
  onOpenActivities,
}: {
  routineRow: RoutineRowViewModel;
  isRoutineSharingEnabled: boolean;
  onOpenActivities: ({ routineId }: { routineId: string }) => void;
}) => {
  const { store } = useAppStore();
  const [isSharing, setIsSharing] = useState(false);
  const [isLoadingShareLink, setIsLoadingShareLink] = useState(false);
  const [isRevokingShare, setIsRevokingShare] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [routineShareLink, setRoutineShareLink] = useState<RoutineShareLink | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const { routine } = routineRow;
  const routineLabel = routine.label || "Untitled routine";
  const descriptionLabel = routine.description || "No description yet.";
  const statusLabel = formatStatusLabel({ status: routine.status });
  const nextRunLabel = formatAgentDate({ value: routine.nextRunAt, includeTime: true });
  const lastRunLabel = formatAgentDate({ value: routine.lastRunAt, includeTime: true });
  const isActive = routine.status === AssistantRoutineStatus.Active;
  const isPaused = routine.status === AssistantRoutineStatus.Paused;
  const routineShareUrl = routineShareLink
    ? windowModule.buildUrl({ path: routineShareLink.share_path }).toString()
    : null;
  const shareButtonLabel = (() => {
    if (routineShareLink) return "Copy link";
    if (isLoadingShareLink) return "Loading...";
    if (isSharing) return "Creating...";
    return "Create and copy link";
  })();

  const copyShareLink = async (link: RoutineShareLink) => {
    const shareUrl = windowModule.buildUrl({ path: link.share_path });
    await copyUrlToClipboard({ url: shareUrl.toString() });
  };

  const handleOpenShareDialog = async () => {
    trackEvent(TrackedEvent.MemAgentShareDialogOpen, { item_kind: "routine", item_id: routine.id });
    setRoutineShareLink(null);
    setIsShareDialogOpen(true);
    setIsLoadingShareLink(true);
    try {
      const activeLink = await getActiveRoutineShareLinkForSourceRoutine({
        sourceRoutineId: routine.id,
      });
      setRoutineShareLink(activeLink);
    } catch {
      toastModule.triggerToast({ content: "Couldn't load routine link" });
    } finally {
      setIsLoadingShareLink(false);
    }
  };

  const handleShare = async () => {
    setIsSharing(true);

    try {
      const link = await createRoutineShareLink({ sourceRoutineId: routine.id });
      trackEvent(TrackedEvent.MemAgentShareLinkCreate, {
        item_kind: "routine",
        item_id: routine.id,
      });
      setRoutineShareLink(link);
      await copyShareLink(link);
    } catch {
      toastModule.triggerToast({ content: "Couldn't create routine link" });
    } finally {
      setIsSharing(false);
    }
  };
  const handleCopyExistingShare = async () => {
    if (!routineShareLink) return;
    trackEvent(TrackedEvent.MemAgentShareLinkCopy, { item_kind: "routine", item_id: routine.id });
    await copyShareLink(routineShareLink);
  };
  const handleRevokeShare = async () => {
    if (!routineShareLink) return;
    setIsRevokingShare(true);
    try {
      await revokeRoutineShareLink({ linkId: routineShareLink.id });
      trackEvent(TrackedEvent.MemAgentShareLinkRevoke, {
        item_kind: "routine",
        item_id: routine.id,
      });
      setRoutineShareLink(null);
      setIsShareDialogOpen(false);
    } catch {
      toastModule.triggerToast({ content: "Couldn't revoke routine link" });
    } finally {
      setIsRevokingShare(false);
    }
  };
  const handleStatusToggle = async () => {
    if (!isActive && !isPaused) return;
    const nextStatus = isActive ? AssistantRoutineStatus.Paused : AssistantRoutineStatus.Active;
    setIsUpdatingStatus(true);
    try {
      await new UpdateAssistantRoutineOperation({
        store,
        payload: {
          id: routine.id,
          status: nextStatus,
        },
      }).execute();
    } finally {
      setIsUpdatingStatus(false);
    }
  };
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await new DeleteAssistantRoutineOperation({
        store,
        payload: {
          id: routine.id,
        },
      }).execute();
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <StyledRoutineCardItem
      aria-label={[
        `Routine: ${routineLabel}`,
        `Description: ${descriptionLabel}`,
        `Status: ${statusLabel}`,
        `Next run: ${nextRunLabel === "—" ? "Not scheduled" : nextRunLabel}`,
        `Last run: ${lastRunLabel === "—" ? "Never" : lastRunLabel}`,
      ].join("; ")}
      role="listitem"
    >
      <StyledRoutineCard variant={MDSCardVariant.Chrome}>
        <StyledCardHeader>
          <StyledRoutineTitle
            color={MDSTextColor.Secondary}
            variant={MDSTextVariant.TextM}
            weight={MDSTextWeight.Smedium}
          >
            {routineLabel}
          </StyledRoutineTitle>

          <StyledStatus>
            <MDSIcon
              color={isActive ? MDSIconColor.Positive : MDSIconColor.Secondary}
              kind={getAgentStatusIconKind({ status: routine.status })}
            />
            <MDSText
              color={isActive ? MDSTextColor.Positive : MDSTextColor.Secondary}
              variant={MDSTextVariant.TextS}
            >
              {statusLabel}
            </MDSText>
          </StyledStatus>
        </StyledCardHeader>

        <StyledCardSeparator orientation="horizontal" />

        <StyledRoutineDescription
          color={routine.description ? MDSTextColor.Secondary : MDSTextColor.Tertiary}
          variant={MDSTextVariant.ParagraphS}
        >
          {descriptionLabel}
        </StyledRoutineDescription>

        <StyledRunMetadata>
          <RoutineMetadata label="Next run" value={nextRunLabel} />
          <RoutineMetadata label="Last run" value={lastRunLabel} />
        </StyledRunMetadata>

        <StyledActions>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Transparent}
            onPress={() => {
              onOpenActivities({ routineId: routine.id });
            }}
          >
            Activity
          </MDSButton>
          {isActive || isPaused ? (
            <MDSButton
              isDisabled={isUpdatingStatus}
              size={MDSButtonSize.Small}
              variant={MDSButtonVariant.Transparent}
              onPress={() => void handleStatusToggle()}
            >
              {isActive ? "Pause" : "Resume"}
            </MDSButton>
          ) : null}
          {isActive && isRoutineSharingEnabled ? (
            <MDSButton
              size={MDSButtonSize.Small}
              variant={MDSButtonVariant.Transparent}
              onPress={() => void handleOpenShareDialog()}
            >
              Share
            </MDSButton>
          ) : null}
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Danger}
            onPress={() => {
              setIsDeleteDialogOpen(true);
            }}
          >
            Delete
          </MDSButton>
        </StyledActions>
      </StyledRoutineCard>
      <MDSModalDialog
        isOpen={isShareDialogOpen}
        handleClose={() => {
          setIsShareDialogOpen(false);
        }}
      >
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          {`Share “${routineLabel}”`}
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Anyone with this link and a Mem account can save a copy of this routine.
          </MDSText>
          {routineShareUrl ? <StyledShareUrl>{routineShareUrl}</StyledShareUrl> : null}
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          {routineShareLink ? (
            <MDSButton
              isDisabled={isRevokingShare}
              variant={MDSButtonVariant.Danger}
              onPress={() => void handleRevokeShare()}
            >
              Revoke link
            </MDSButton>
          ) : null}
          <MDSButton
            variant={MDSButtonVariant.Transparent}
            onPress={() => {
              setIsShareDialogOpen(false);
            }}
          >
            Close
          </MDSButton>
          <MDSButton
            isDisabled={isSharing || isLoadingShareLink}
            variant={MDSButtonVariant.Contrast}
            onPress={() => {
              if (routineShareLink) {
                void handleCopyExistingShare();
                return;
              }
              void handleShare();
            }}
          >
            {shareButtonLabel}
          </MDSButton>
        </MDSButtonGroup>
      </MDSModalDialog>

      <MDSModalDialog
        isOpen={isDeleteDialogOpen}
        handleClose={() => {
          setIsDeleteDialogOpen(false);
        }}
      >
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          {`Delete “${routineLabel}”?`}
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Deletion is permanent and cannot be undone.
          </MDSText>
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton
            variant={MDSButtonVariant.Transparent}
            onPress={() => {
              setIsDeleteDialogOpen(false);
            }}
          >
            Cancel
          </MDSButton>
          <MDSButton
            isDisabled={isDeleting}
            variant={MDSButtonVariant.DangerFilled}
            onPress={() => void handleDelete()}
          >
            Delete forever
          </MDSButton>
        </MDSButtonGroup>
      </MDSModalDialog>
    </StyledRoutineCardItem>
  );
};

const RoutineMetadata = ({ label, value }: { label: string; value: string }) => (
  <StyledMetadataItem>
    <MDSText
      color={MDSTextColor.Primary}
      variant={MDSTextVariant.TextS}
      weight={MDSTextWeight.SemiBold}
    >
      {label}:
    </MDSText>
    <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
      {value}
    </MDSText>
  </StyledMetadataItem>
);

const StyledRoutineCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  height: 100%;
  min-height: 176px;
  min-width: 0;
`;

const StyledRoutineCardItem = styled.div`
  min-width: 0;
`;

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  min-width: 0;
`;

const StyledRoutineTitle = styled(MDSText)`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const StyledStatus = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const StyledCardSeparator = styled(MDSSeparator)`
  margin: 0;
`;

const StyledRoutineDescription = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledRunMetadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[5]};
`;

const StyledMetadataItem = styled.div`
  align-items: baseline;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing[3]};
`;

const StyledShareUrl = styled.div`
  background: ${({ theme }) => theme.colors.background.button.chip.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  margin-top: ${({ theme }) => theme.spacing[3]};
  overflow-wrap: anywhere;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
`;
